/**
 * PostVisitSummaryDialog — read-only PVS render + Print/Download action.
 *
 * First encounter_module carve-out into PM (PROD-874, Epic PROD-873).
 * Design revision PROD-886 (Andrew 2026-05-21) — header bar + two-column
 * layout + section icons + 15-slot vitals grid + Scrips Community card.
 *
 * Flutter ref: `encounter_module/encounter_wrap_up/presentation/widgets/post_visit_summary_standalone.dart`
 *
 * Color rule: all decorative section icons use Signal DS brand-primary
 * (Blue/500) per the "use our color coding" directive. Medication-bucket
 * arrows keep the clinical-signal palette (success/warning/info/error)
 * carved out in PR 1.
 *
 * Vitals grid: 15 base slots + optional LMP (female + age > 8) + optional
 * HCM (age ≤ 2). Mirrors Flutter `emptyVitalUiList` in
 * `patient_module/patient_chart/data/datamodels/ui_data.dart:1184`.
 *
 * Design sheet v1.1 §2.9 (PROD-2743, PR-4) — the design look is unconditional; no switch
 * chooses it.
 * Person-first title block with the MRN last and quiet (I10); brand headings → DS
 * `panel-title` / `card-title` / `section-eyebrow` in text-secondary (R7); section and bucket
 * glyphs → DS `Icon` names; the vitals grid → DS `VitalIcon` kinds (R1 #17); the two body cards
 * lift with the card border + `--shadow-2` (R2 A ×2). The section badge keeps today's
 * `bg-blue-100` + brand glyph in both modes: the solid wayfinding badge (H6) needs DS 2.14.0
 * tokens this repo does not pin yet (2.13.2). Clinical content is identical in both modes.
 * Print/Download is the server PDF (`/api/Patients/PVSPrint`), not this DOM, so the switch does
 * not reach the printed document.
 */

import { Fragment, useState } from 'react';
import {
  ArrowDown,
  ArrowRight,
  BookOpen,
  CalendarDays,
  ClipboardList,
  Dna,
  Ear,
  Eye,
  FlaskConical,
  Heart,
  Ruler,
  StopCircle, // GAP: no DS glyph for "stopped" (StopCircle), design sheet v1.1 §3 — lucide in both modes
  Syringe,
  Scale,
  Thermometer,
  RefreshCw,
  Wind,
  Pill,
  Activity,
  Droplets,
  NotebookText,
  type LucideIcon,
} from 'lucide-react';
import {
  Modal,
  Skeleton,
  TEXT_ROLE_CLASSES,
  TEXT_TONE_CLASSES,
  VitalIcon,
  cn,
  type VitalKind,
} from '@scripsteam/scrips-signal-ds';
import {
  V2_BODY_EMPHASIS,
  V2_EYEBROW,
  V2_META,
} from '@/encounter/lib/design-v2';
import { prepForLab, type LabPrintExtras } from '@/encounter/lib/lab-print-extras';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import {
  usePostVisitSummary,
  usePVSPrint,
  type PvsLookup,
} from '@/patients/services/use-post-visit-summary';
import type {
  PostVisitSummaryData,
  PvsArticle,
  PvsLab,
  PvsNarrativeSection,
  PvsProcedure,
  PvsRadiology,
  PvsStopTakingMedication,
  PvsTakingMedication,
  PvsPatient,
  PvsVaccination,
} from '@/types/post-visit-summary';

// ─── Design sheet v1.1 · switch-ON class strings (DS roles/tones/tokens only) ────────────────────

/** §1 Type: a dialog/document title is the DS `panel-title` (18 semibold, text-primary). */
const PANEL_TITLE_V2 = TEXT_ROLE_CLASSES['panel-title'];
/** §1 Type: a card heading is the DS `card-title` (15 semibold, text-primary). */
const CARD_TITLE_V2 = TEXT_ROLE_CLASSES['card-title'];
/** §1 Identity: the quiet facts after the name (age · sex · DOB) — `body`, inked text-secondary. */
const IDENTITY_FACT_V2 = cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.secondary);
/** §1 Surfaces: the card lifts — card border + `--shadow-2`. Radius and padding unchanged. */
const BODY_CARD_V2 =
  'bg-surface-card border border-border-card shadow-2 rounded-lg p-6 grid md:grid-cols-summary-aside gap-8';
const SKELETON_CARD_V2 = 'bg-surface-card border border-border-card shadow-2 rounded-lg p-6 space-y-4';
/** §1 Type/Label colour: section titles are the caps label in text-secondary, never brand. */
const SECTION_TITLE_V2 = `flex items-center gap-2 mb-2 ${V2_EYEBROW}`;

export interface PostVisitSummaryDialogProps extends PvsLookup {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * PROD-2849 (wrap-up's order view): each lab order's patient prep, and the tests that go as Patient's
   * choice of lab, listed for the patient to take to any lab. Absent outside the encounter.
   */
  labExtras?: LabPrintExtras;
}

export function PostVisitSummaryDialog({
  open,
  onOpenChange,
  encounterId,
  appointmentId,
  labExtras,
}: PostVisitSummaryDialogProps) {
  const enabled = open && !!(encounterId || appointmentId);

  const query = usePostVisitSummary(
    enabled ? { encounterId, appointmentId } : { encounterId: null },
  );
  const print = usePVSPrint();
  const [printErrored, setPrintErrored] = useState(false);

  const data = query.data ?? null;

  const handlePrint = () => {
    if (!encounterId && !appointmentId) return;
    setPrintErrored(false);
    print.mutate(
      { encounterId, appointmentId },
      { onError: () => setPrintErrored(true) },
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={<span className="sr-only">Post Visit Summary</span>}
      /* Sized in ZOOM-CORRECTED viewport units by the DS Modal: `size="summary"` is
       * min(1100px, 95% of --app-vw) and `maxHeight="app-viewport"` is 90% of --app-vh. PA applies a
       * root density scale and viewport units do not compensate for root zoom, so bare vw/vh painted
       * ~10% small (Andrew, 2026-08-26); the DS steps read the zoom-corrected --app-vw/--app-vh, and
       * fall back to plain 100vw/100vh where they are undefined (PM). The header scrolls with the
       * body as one block, up to the cap (Modal's own overflow-auto). */
      size="summary"
      maxHeight="app-viewport"
      className="p-0"
    >
        {/* Header bar — Done · centered title · Print. Mirrors Flutter
            `post_visit_summary_standalone.dart:120-178` exactly. */}
        <header data-pvs="bar" className="flex items-center justify-between px-6 py-4 border-b border-border-default">
          <button
            type="button"
            data-pvs="bar-btn"
            onClick={() => onOpenChange(false)}
            className="text-base font-semibold text-brand-primary hover:opacity-80 transition-opacity"
          >
            Done
          </button>
          <div className="flex-1 text-center min-w-0 px-4">
            <div
              data-pvs="bar-reason"
              className={
                `${PANEL_TITLE_V2} truncate`
              }
            >
              {data?.visitReason?.trim() || ''}
            </div>
            <div data-pvs="bar-date" className="text-xs text-text-muted truncate">
              {data?.encounterDate?.trim() || ''}
            </div>
          </div>
          <button
            type="button"
            data-pvs="bar-btn"
            onClick={handlePrint}
            disabled={!data || print.isPending}
            className="text-base font-semibold text-brand-primary hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {print.isPending ? 'Preparing…' : 'Print'}
          </button>
        </header>

        <div data-pvs="scroll" className="overflow-y-auto scrollbar-hide flex-1 px-6 py-5 bg-grey-100/30">
          {query.isPending ? (
            <LoadingSkeleton />
          ) : query.isError ? (
            <EmptyBlock>Could not load the Post-Visit Summary. Try again later.</EmptyBlock>
          ) : !data ? (
            <EmptyBlock>No Post-Visit Summary is available for this encounter.</EmptyBlock>
          ) : (
            <PvsBody data={data} labExtras={labExtras} />
          )}
          {printErrored ? (
            <p className="mt-4 text-sm text-status-error">
              Could not open the PDF. Check your popup blocker and try again.
            </p>
          ) : null}
        </div>
    </Modal>
  );
}

// ─── Body ────────────────────────────────────────────────────────────────────

function PvsBody({ data, labExtras }: { data: PostVisitSummaryData; labExtras?: LabPrintExtras }) {
  // Two columns from `md` up: sections left, clinic block + Today's Visit + vitals right.
  // `grid-cols-1` was REMOVED, and that is the fix for the PA layout (Andrew, 2026-08-27: the PVS
  // renders two columns in PM and collapsed to one in PA, dropping the vitals grid to the bottom).
  // Measured in PA: `md:grid-cols-summary-aside` (1fr 300px) ALONE resolves to "500px 300px", but paired with
  // `grid-cols-1` the base class wins and the template collapses to a single track — Tailwind v4
  // emits the variant as a nested `@media` inside the class rule, which adds no specificity, so the
  // two rules are decided by cascade alone and PA resolves it the other way from PM.
  // Dropping the base class costs nothing: a grid with no explicit template already lays its items
  // out in one implicit column below `md`. Do not reinstate `grid-cols-1` "for clarity" — it is
  // what broke this.
  return (
    <div
      data-pvs="card"
      className={BODY_CARD_V2}
    >
      {/* Left — main content */}
      <div className="space-y-6 min-w-0">
        <TitleBlock data={data} />
        <DoctorInfo data={data} />
        {/* The signed template answers, behind ?x=pvs-visit-notes (off by default) until reviewed. */}
        {isEncounterFlagOn('pvsVisitNotes') ? (
          <VisitNotesSection sections={data.templateNarrative ?? []} unavailable={data.templateNarrativeUnavailable === true} />
        ) : null}
        <InstructionsSection text={data.instructions ?? null} />
        <ImmunizationSection items={data.vaccinations ?? []} />
        <MedicationsSection
          start={data.startTakingMedications ?? []}
          change={data.changeTakingMedications ?? []}
          continueMeds={data.continueTakingMedications ?? []}
          stop={data.stopTakingMedications ?? []}
        />
        <FollowUpSection
          duration={data.wrapup?.followupDuration ?? null}
          date={data.wrapup?.followupDate ?? null}
        />
        <OrdersSection
          labExtras={labExtras}
          labs={data.labs ?? []}
          radiologies={data.radiologies ?? []}
          procedures={data.procedures ?? []}
        />
        <ArticlesSection items={data.articles ?? []} />
      </div>

      {/* Right — practice + today's visit + community */}
      <aside className="space-y-6">
        <PracticeSidebar data={data} />
        <TodaysVisitPanel data={data} />
        <ScripsCommunityCard />
      </aside>
    </div>
  );
}

function TitleBlock({ data }: { data: PostVisitSummaryData }) {
  const patient = data.patient ?? null;
  const policy = data.policy?.trim();
  const eRx = data.eRx?.trim();

  return (
    <div>
      <h2 className={PANEL_TITLE_V2}>
        Post Visit Summary
      </h2>
      <PvsIdentityLineV2 patient={patient} />
      {(policy || eRx) ? (
        <p className="text-sm text-text-primary leading-relaxed">
          {policy ? `| Member ID : ${policy}` : ''}
          {policy && eRx ? '  ' : ''}
          {eRx ? `| eRx # : ${eRx}` : ''}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Design sheet v1.1 §1 Identity (D-27, A14) + sweep I10: person first. The name
 * leads (`body-emphasis`), then age · sex · DOB as quiet `body` text-secondary, then the MRN ONCE,
 * LAST, as `meta` (11, text-secondary), never bold or uppercase. The values and the DOB string are
 * exactly the ones the OFF line prints (same trims, same age-suffix rule, same "DOB: " date text);
 * only order and weight change. Sex shows only when the record carries `genderName`: nothing is
 * inferred.
 */
function PvsIdentityLineV2({ patient }: { patient: PvsPatient | null }) {
  const name = patient?.patientName?.trim();
  const mrn = patient?.mrn?.trim();
  const facts: string[] = [];
  if (patient?.age?.trim()) facts.push(`${patient.age.trim().replace(/[yY]+$/, '')}y`);
  if (patient?.genderName?.trim()) facts.push(patient.genderName.trim());
  if (patient?.dob?.trim()) facts.push(`DOB: ${patient.dob.trim()}`);

  const items: Array<{ key: string; text: string; className: string }> = [];
  if (name) items.push({ key: 'name', text: name, className: V2_BODY_EMPHASIS });
  facts.forEach((text, i) => items.push({ key: `fact-${i}`, text, className: IDENTITY_FACT_V2 }));
  if (mrn) items.push({ key: 'mrn', text: mrn, className: V2_META });

  return (
    <p className="mt-2 leading-relaxed" data-pvs-identity="line">
      {items.length === 0 ? (
        <span className={IDENTITY_FACT_V2}>—</span>
      ) : (
        items.map((item, i) => (
          <Fragment key={item.key}>
            {i > 0 ? (
              <span className={IDENTITY_FACT_V2} aria-hidden="true">
                {' · '}
              </span>
            ) : null}
            <span className={item.className} data-pvs-identity={item.key === 'mrn' ? 'mrn' : undefined}>
              {item.text}
            </span>
          </Fragment>
        ))
      )}
    </p>
  );
}

function DoctorInfo({ data }: { data: PostVisitSummaryData }) {
  const provider = data.provider ?? null;
  return (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-full bg-grey-100 overflow-hidden shrink-0">
        {provider?.providerPhoto ? (
          <img
            src={provider.providerPhoto}
            alt=""
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : null}
      </div>
      <div className="min-w-0">
        <div data-pvs="doctor-name" className={V2_BODY_EMPHASIS}>
          {provider?.providerName?.trim() || '—'}
        </div>
        <div className="text-sm text-text-primary">
          {provider?.providerSpeciality?.trim() || ''}
        </div>
      </div>
    </div>
  );
}

/**
 * The signed template answers (PROD-2531), as the printed PDF's "Visit Notes" shows them
 * (`PVSTemplate.html`): each answered section's title, then "Label: value" lines, text as
 * written. An entry with no value ("Allergies:" and nothing after it) is left out, and so is a
 * section left with no answered line. Nothing when the visit was documented without a template; whenever QuestionBank could not
 * be read (`templateNarrativeUnavailable`), a line saying the notes could not be loaded, also under
 * any sections that did come back. Read-only.
 */
function VisitNotesSection({ sections, unavailable }: { sections: PvsNarrativeSection[]; unavailable: boolean }) {
  const shown = sections
    .map((s) => ({
      title: s.title?.trim() ?? '',
      entries: (s.entries ?? []).filter((e) => e.value?.trim()),
    }))
    .filter((s) => s.entries.length > 0);
  if (shown.length === 0 && !unavailable) return null;
  return (
    <Section title="Visit notes" icon={NotebookText}>
      <div className="space-y-4">
        {shown.map((s, i) => (
          <div key={`${s.title}-${i}`}>
            {s.title ? <p className="text-sm font-semibold text-text-primary break-words">{s.title}</p> : null}
            <ul className="mt-1 space-y-1">
              {s.entries.map((e, j) => (
                <li key={`${e.label ?? ''}-${j}`} className="text-sm text-text-primary whitespace-pre-wrap break-words">
                  {e.label?.trim() ? <span className="font-medium">{`${e.label.trim()}: `}</span> : null}
                  {e.value ?? ''}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {unavailable ? <EmptyText>Visit notes could not be loaded for this summary.</EmptyText> : null}
      </div>
    </Section>
  );
}

function InstructionsSection({ text }: { text: string | null }) {
  const value = text?.trim();
  return (
    <Section title="Instructions" icon={ClipboardList}>
      <RichTextBlock text={value} fallback="No instructions" />
    </Section>
  );
}

function ImmunizationSection({ items }: { items: PvsVaccination[] }) {
  return (
    <Section title="Immunization" icon={Syringe}>
      {items.length === 0 ? (
        <EmptyText>No administration</EmptyText>
      ) : (
        <ul className="space-y-3">
          {items.map((v, i) => (
            <li key={`${v.vaccinationCode ?? ''}-${i}`}>
              <p className="text-sm font-semibold text-text-primary">
                {`Administered Vaccine of ${v.vaccinationName?.trim() || '—'}`}
              </p>
              {(v.vaccinationDose?.trim() || v.vaccinationLotNo?.trim()) ? (
                <p className="text-sm text-text-primary">
                  {v.vaccinationDose?.trim() ? `${v.vaccinationDose.trim()}, ` : ''}
                  {v.vaccinationLotNo?.trim()
                    ? `Lot no. ${v.vaccinationLotNo.trim()}`
                    : ''}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function MedicationsSection({
  start,
  change,
  continueMeds,
  stop,
}: {
  start: PvsTakingMedication[];
  change: PvsTakingMedication[];
  continueMeds: PvsTakingMedication[];
  stop: PvsStopTakingMedication[];
}) {
  const empty =
    start.length === 0 &&
    change.length === 0 &&
    continueMeds.length === 0 &&
    stop.length === 0;

  return (
    <Section title="Today's Medication Changes" icon={Pill}>
      {empty ? (
        <EmptyText>No changes</EmptyText>
      ) : (
        <div className="space-y-4">
          {start.length > 0 ? (
            <BucketBlock
              label="START Taking"
              tone="success"
              icon={ArrowRight}
              items={start}
            />
          ) : null}
          {change.length > 0 ? (
            <BucketBlock
              label="CHANGE how you take"
              tone="warning"
              icon={RefreshCw}
              items={change}
            />
          ) : null}
          {continueMeds.length > 0 ? (
            <BucketBlock
              label="CONTINUE Taking"
              tone="info"
              icon={ArrowDown}
              items={continueMeds}
            />
          ) : null}
          {/* GAP: no DS glyph for "stopped" (StopCircle), design sheet v1.1 §3 — no `dsIcon`, so this
              bucket keeps the lucide StopCircle in both modes. */}
          {stop.length > 0 ? (
            <BucketBlock
              label="STOP Taking"
              tone="danger"
              icon={StopCircle}
              items={stop.map((s) => ({
                name: s.name,
                description: s.stopReson,
              }))}
            />
          ) : null}
        </div>
      )}
    </Section>
  );
}

function BucketBlock({
  label,
  tone,
  icon: Glyph,
  items,
}: {
  label: string;
  tone: 'success' | 'warning' | 'info' | 'danger';
  /** The bucket's Lucide glyph (icon pass, 16 Oct: one library in both modes). */
  icon: LucideIcon;
  items: Array<{ name?: string | null; description?: string | null }>;
}) {
  // Clinical-signal carve-out for treatment intent (PR 1 review).
  const toneClass =
    tone === 'success'
      ? 'text-green-700'
      : tone === 'warning'
        ? 'text-status-amber-text'
        : tone === 'danger'
          ? 'text-red-700'
          : 'text-blue-700';

  return (
    <div>
      <p className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide ${toneClass}`}>
        <Glyph className="size-4" aria-hidden="true" />
        {label}
      </p>
      <ul className="mt-2 space-y-2 pl-6">
        {items.map((m, i) => (
          <li key={`${m.name ?? ''}-${i}`}>
            <p className="text-sm font-semibold text-text-primary">
              {m.name?.trim() || '—'}
            </p>
            <p className="text-sm text-text-primary whitespace-pre-wrap">
              {(m.description ?? '').replace(/<br\s*\/?\s*>/gi, '\n')}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FollowUpSection({
  duration,
  date,
}: {
  duration: string | null;
  date: string | null;
}) {
  const hasNothing = !duration && !date;
  return (
    <Section title="Come back for follow up" icon={CalendarDays}>
      {hasNothing ? (
        <EmptyText>No follow up</EmptyText>
      ) : (
        <p className="text-sm text-text-primary whitespace-pre-line">
          {`In ${duration ?? '—'}  |  Around ${date ?? '—'}\nYou can schedule via Scrips app or reception.`}
        </p>
      )}
    </Section>
  );
}

function OrdersSection({
  labs,
  radiologies,
  procedures,
  labExtras,
}: {
  labs: PvsLab[];
  radiologies: PvsRadiology[];
  procedures: PvsProcedure[];
  labExtras?: LabPrintExtras;
}) {
  const rows: Array<{ kind: string; title: string; info: string | null; prep?: string | null }> = [
    ...labs.map((l) => ({
      kind: 'lab',
      title: `${l.labCode ?? ''} • ${l.labName ?? ''}`,
      info: l.labNotes ?? null,
      // PROD-2849: the lab order's patient prep ("Fasting 8 h preferred").
      prep: prepForLab(labExtras?.preps, l.labCode, l.labName),
    })),
    ...radiologies.map((r) => ({
      kind: 'radiology',
      title: `${r.radiologyCode ?? ''} • ${r.radiologyName ?? ''}`,
      info: r.radiologyNotes ?? null,
    })),
    ...procedures.map((p) => ({
      kind: 'procedure',
      title: `${p.procedureCode ?? ''} • ${p.procedureName ?? ''}`,
      info: p.procedureNotes ?? null,
    })),
  ];

  return (
    <Section title="Recommended Orders" icon={FlaskConical}>
      {rows.length === 0 ? (
        <EmptyText>No orders</EmptyText>
      ) : (
        <ul className="space-y-3">
          {rows.map((r, i) => (
            <li key={`${r.kind}-${i}`}>
              <p className="text-sm font-semibold text-text-primary">
                {r.title.replace(/^ • | • $/g, '').trim() || '—'}
              </p>
              {r.prep ? (
                <p className="text-sm text-text-secondary" data-testid="pvs-lab-prep">
                  Patient prep: {r.prep}
                </p>
              ) : null}
              {r.info?.trim() ? (
                <RichTextBlock text={r.info} fallback="" />
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {/* PROD-2849: Patient's choice of lab. Not sent or billed by the clinic; the patient takes these
          to any lab (canvas board 8: "The order prints from wrap-up, including the Patient's choice test"). */}
      {labExtras && labExtras.patientsChoiceTests.length > 0 ? (
        <div className="mt-4" data-testid="pvs-patients-choice">
          <p className="text-sm font-semibold text-text-primary">Patient&apos;s choice of lab</p>
          <p className="text-sm text-text-secondary">
            Take these tests to any lab. They are not sent or billed by the clinic.
          </p>
          <ul className="mt-1 list-disc pl-6">
            {labExtras.patientsChoiceTests.map((t) => (
              <li key={t} className="text-sm text-text-primary">
                {t}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Section>
  );
}

function ArticlesSection({ items }: { items: PvsArticle[] }) {
  return (
    <Section title="Read the attached information" icon={BookOpen}>
      {items.length === 0 ? (
        <EmptyText>No information</EmptyText>
      ) : (
        <ul className="space-y-2">
          {items.map((a, i) => (
            <li key={`${a.articleUrl ?? ''}-${i}`}>
              {a.articleUrl?.trim() ? (
                <a
                  href={a.articleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-brand-primary hover:underline"
                >
                  {a.articleName?.trim() || a.articleUrl}
                </a>
              ) : (
                <span className="text-sm text-text-primary">
                  {a.articleName?.trim() || '—'}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function PracticeSidebar({ data }: { data: PostVisitSummaryData }) {
  return (
    <div className="text-right">
      <div className="flex justify-end mb-3">
        {data.organizationPhoto?.trim() ? (
          <img
            src={data.organizationPhoto}
            alt=""
            className="h-10 w-auto object-contain"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-10 w-10 rounded-full bg-grey-100" aria-hidden="true" />
        )}
      </div>
      <p className="text-sm font-bold text-text-primary">
        {data.practiceName?.trim() || '—'}
      </p>
      <p className="text-sm text-text-primary mt-1">
        {data.practiceAddress?.trim() || '—'}
      </p>
      {data.practicePhone?.trim() ? (
        <p className="text-sm text-text-primary mt-1">
          {data.practicePhone.trim()}
        </p>
      ) : null}
      {data.organizationName?.trim() ? (
        <p className="text-sm text-text-primary mt-3">
          {`Thank you for choosing ${data.organizationName.trim()}`}
        </p>
      ) : null}
    </div>
  );
}

function TodaysVisitPanel({ data }: { data: PostVisitSummaryData }) {
  const provider = data.provider?.providerName?.trim();
  const time = data.encounterTime?.trim();
  const date = data.encounterDate?.trim();
  const reason = data.visitReason?.trim();

  const narrative =
    provider && time && date && reason
      ? `You saw ${provider} at ${time} on ${date} for ${reason}.`
      : null;

  const diagnoses = (data.diagnoses ?? [])
    .map((d) => d.diagnosisName?.trim())
    .filter((s): s is string => Boolean(s));

  const vitalsValueByCode = new Map<string, string>();
  for (const v of data.vitals ?? []) {
    const code = v.vitalCode?.trim();
    const value = v.vitalValue?.trim();
    if (code && value) vitalsValueByCode.set(code, value);
  }

  const slots = buildVitalSlots({
    gender: data.patient?.genderName ?? null,
    age: parseAgeYears(data.patient?.age ?? null, data.patient?.dob ?? null),
  });

  return (
    <div data-pvs="panel" className="rounded-lg bg-grey-100/50 p-4">
      <h3 className={CARD_TITLE_V2}>
        Today&apos;s Visit
      </h3>
      {narrative ? (
        <p className="text-sm text-text-primary mt-2">{narrative}</p>
      ) : null}
      {diagnoses.length > 0 ? (
        <>
          <p className="text-sm text-text-primary mt-2">
            The following issues were addressed:
          </p>
          <ul className="text-sm text-text-primary mt-1 space-y-1">
            {diagnoses.map((d, i) => (
              <li key={i}>• {d}</li>
            ))}
          </ul>
        </>
      ) : null}

      <h4
        className={`${V2_EYEBROW} mt-6`}
      >
        Your Vitals
      </h4>
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
        {slots.map((s) => {
          const value = (s.wireCodes ?? [s.code]).map((c) => vitalsValueByCode.get(c)).find(Boolean) ?? null;
          return <VitalCell key={s.code} slot={s} value={value} />;
        })}
      </ul>
    </div>
  );
}

function VitalCell({ slot, value }: { slot: VitalSlot; value: string | null }) {
  return (
    <li className="flex items-start gap-2">
      {/* The DS VitalIcon for this vital (decorative: the DS sets aria-hidden), 16 inline,
          inked by context (`currentColor` from the muted class), never a status colour. */}
      <VitalIcon kind={slot.kind} color="currentColor" className="h-4 w-4 text-text-muted mt-1 shrink-0" />
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {slot.label}
        </div>
        <div className="text-sm text-text-primary mt-1">
          {value?.trim() ? value.trim() : '—'}
          {slot.unit ? <span className="text-text-muted"> {slot.unit}</span> : null}
        </div>
      </div>
    </li>
  );
}

function ScripsCommunityCard() {
  return (
    <div data-pvs="panel" className="rounded-lg bg-grey-100/50 p-4 relative">
      <h3 className={CARD_TITLE_V2}>
        Be Part of Scrips Community
      </h3>
      <p className="text-sm text-text-primary mt-2 leading-relaxed">
        Access your invoice and other medical documents in your Scrips
        account. We value your health beyond clinical visits. There is so
        much our community based 360 degrees of care can offer you.
      </p>
      <div
        className="text-sm font-bold text-brand-primary mt-4 text-right tracking-wider"
        aria-label="Scrips"
      >
        scrips
      </div>
    </div>
  );
}

// ─── Vitals slot config ──────────────────────────────────────────────────────

interface VitalSlot {
  code: string;
  label: string;
  unit: string;
  /** OFF glyph (today). */
  icon: LucideIcon;
  /** ON glyph: the DS `VitalIcon` kind for this vital (sweep plan R1 #17 / line 59). */
  kind: VitalKind;
  /** Wire codes to try, in order, when the API does not send this vital under `code`. */
  wireCodes?: string[];
}

/**
 * Build the canonical vitals slot list. Mirrors Flutter `emptyVitalUiList`
 * (`patient_module/patient_chart/data/datamodels/ui_data.dart:1184`).
 *
 * Base list is 15 slots; LMP adds when female + age > 8; HCM adds when
 * age ≤ 2. Codes match Flutter `AppConstants.*VitalId` so the wire
 * lookup against `data.vitals[*].vitalCode` works.
 */
function buildVitalSlots({
  gender,
  age,
}: {
  gender: string | null;
  age: number | null;
}): VitalSlot[] {
  const base: VitalSlot[] = [
    { code: VITAL_CODES.temp, label: 'Temp', unit: '°C', icon: Thermometer, kind: 'temp' },
    { code: VITAL_CODES.pulse, label: 'Pulse', unit: '', icon: Heart, kind: 'pulse' },
    { code: VITAL_CODES.systolic, label: 'BP', unit: '', icon: Activity, kind: 'bp' },
    { code: VITAL_CODES.respRate, label: 'Resp Rate', unit: '', icon: Wind, kind: 'resp' },
    { code: VITAL_CODES.o2Sat, label: 'O2 Sat', unit: '%', icon: Droplets, kind: 'o2' },
    { code: VITAL_CODES.height, label: 'Height', unit: 'cm', icon: Ruler, kind: 'height' },
    { code: VITAL_CODES.weight, label: 'Weight', unit: 'kg', icon: Scale, kind: 'weight' },
    // Patient's PVS computes BMI and sends it as VitalCode "BMI" (PostVisitSummaryService.cs:267),
    // not as LOINC 39156-5; the LOINC row is the fallback for a stored reading.
    { code: VITAL_CODES.bmi, label: 'BMI', unit: '', icon: Activity, kind: 'bmi', wireCodes: [PVS_BMI_CODE, VITAL_CODES.bmi] },
    { code: VITAL_CODES.hip, label: 'Hip Circumf', unit: 'cm', icon: Ruler, kind: 'hipCirc' },
    { code: VITAL_CODES.waist, label: 'Waist Circu', unit: 'cm', icon: Ruler, kind: 'waistCirc' },
    { code: VITAL_CODES.waistToHip, label: 'Waist-to-Hip', unit: '', icon: Ruler, kind: 'waistToHipRatio' },
    { code: VITAL_CODES.bioage, label: 'Bioage', unit: '', icon: Dna, kind: 'bioage' },
    { code: VITAL_CODES.visual, label: 'Visual', unit: '', icon: Eye, kind: 'visualAcuity' },
    { code: VITAL_CODES.hearing, label: 'Hearing', unit: '', icon: Ear, kind: 'hearingTest' },
  ];

  if (gender?.toLowerCase() === 'female' && age != null && age > 8) {
    base.push({ code: VITAL_CODES.lmp, label: 'LMP', unit: '', icon: CalendarDays, kind: 'lmp' });
  }
  if (age != null && age <= 2) {
    base.push({ code: VITAL_CODES.hcm, label: 'HCM', unit: 'cm', icon: Ruler, kind: 'headCirc' });
  }
  return base;
}

/**
 * Flutter `AppConstants.*VitalId` literals (app_constants.dart:259-275) — the LOINC codes the
 * wire actually sends, matched against `PatientVitals.vitalId => code?.code`
 * (patient_vitals_model.dart:197).
 *
 * ⚠️ CORRECTED 2026-09-07. These were word literals — 'temp', 'pulse', 'systolic', … — under a
 * comment claiming they had been "copied verbatim" from `AppConstants.*VitalId`. They had not:
 * those constants are LOINC codes. Every lookup therefore missed and **all 14-16 vitals cells
 * rendered the em-dash**, on a patient whose readings were visible elsewhere on the same screen.
 * Captured off dev on 2026-09-07 to settle it, verbatim from
 * `GET /api/Patients/PostVisitSummary`:
 *   vitals: [ {vitalCode:"8310-5",vitalValue:"50"}, {vitalCode:"8867-4",vitalValue:"125"},
 *             {vitalCode:"8480-6",vitalValue:"200"}, {vitalCode:"9279-1",vitalValue:"45"}, … ]
 *
 * This file is byte-identical in PM and PA (see the header note); PM carries the same defect and
 * needs the same correction.
 */
const VITAL_CODES = {
  temp: '8310-5',
  pulse: '8867-4',
  systolic: '8480-6',
  respRate: '9279-1',
  o2Sat: '59408-5',
  height: '8302-2',
  weight: '29463-7',
  bmi: '39156-5',
  hip: '8281-8',
  waist: '8280-0',
  waistToHip: '56115-9',
  bioage: '11-5',
  visual: '31479-8',
  hearing: '62977-4',
  lmp: '8665-2',
  hcm: '11947-9',
} as const;

/** The code Patient's PVS sends its computed BMI under (not a LOINC). */
const PVS_BMI_CODE = 'BMI';

/**
 * Resolve patient age in years from either the API's `age` string
 * (which may be `"27y"` / `"27"` / `"36"`) or fallback to DOB.
 */
function parseAgeYears(
  ageStr: string | null,
  dob: string | null,
): number | null {
  if (ageStr?.trim()) {
    const m = /^(\d+)/.exec(ageStr.trim());
    if (m) return Number(m[1]);
  }
  if (dob?.trim()) {
    const t = Date.parse(dob);
    if (!Number.isNaN(t)) {
      const years = (Date.now() - t) / (1000 * 60 * 60 * 24 * 365.25);
      return Math.floor(years);
    }
  }
  return null;
}

// ─── Primitives ──────────────────────────────────────────────────────────────

function Section({
  title,
  icon: Glyph,
  children,
}: {
  title: string;
  /** The section's Lucide glyph (icon pass, 16 Oct: one library in both modes). */
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3
        className={SECTION_TITLE_V2}
      >
        {/* Icon pass (16 Oct): no filled tile behind a glyph and no decorative blue; the 32 box stays so the body
            keeps its indent under the title. */}
        <span className="inline-flex h-8 w-8 items-center justify-center">
          <Glyph className="size-4 text-text-secondary" aria-hidden="true" />
        </span>
        {title}
      </h3>
      <div className="pl-10">{children}</div>
    </section>
  );
}

function EmptyText({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-text-muted">{children}</p>;
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <div data-pvs="empty" className="bg-surface-card rounded-lg border border-dashed border-border-default p-8 text-center">
      <p className="text-sm text-text-muted">{children}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div data-pvs="skeleton" className={SKELETON_CARD_V2}>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-72" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

/**
 * Plain-text body renderer. Flutter renders this via `QuillStaticView`
 * which we don't ship in PM — strip `<br>` tags to newlines and call
 * it a day. If `instructions` ever ships full HTML, route through
 * `dompurify` (already used elsewhere). For now the wire is plain text
 * or a tiny set of `<br>` tags per Flutter usage.
 */
function RichTextBlock({
  text,
  fallback,
}: {
  text: string | null | undefined;
  fallback: string;
}) {
  const value = (text ?? '').trim();
  if (!value) {
    return fallback ? <EmptyText>{fallback}</EmptyText> : null;
  }
  return (
    <p className="text-sm text-text-primary whitespace-pre-wrap">
      {value.replace(/<br\s*\/?\s*>/gi, '\n')}
    </p>
  );
}
